import { BusinessDate } from '../date/business-date';

/**
 * O prazo da obra visto do dia do relatório (Fase 18, fatia 6).
 *
 * ```text
 * Prazo: 1095 dias   Tempo decorrido: 678 dias   Saldo: 417 dias
 * ```
 *
 * São os três números do cabeçalho do RDO que **ninguém digita**: saem do início e do
 * término previsto da obra, que já estão no cadastro desde a Fase 02. Conferi contra o
 * RDO de referência — 01/11/2024 a 01/11/2027 são os 1095 dias, e 10/09/2026 são os 678
 * decorridos.
 *
 * ## Por que a contagem é a diferença, e não a diferença mais um
 *
 * A tentação é contar dias *inclusive*, e ela quebra a conta do papel: 1095 é a diferença
 * exata entre as duas datas, e `+1` faria o saldo do último dia valer um em vez de zero.
 * O prazo é a **duração**, não a quantidade de folhinhas — é o que faz "faltam 0 dias"
 * cair exatamente no término previsto.
 *
 * ## Saldo negativo é informação, e por isso não é aparado
 *
 * Obra que passou do término previsto tem saldo negativo, e escondê-lo atrás de um zero
 * apagaria justamente o fato que o documento existe para registrar. Quem imprime decide
 * como mostrar; aqui o número é o que é.
 */
export interface ProjectTerm {
  /** `endDate - startDate`, em dias. */
  readonly totalDays: number;
  /** `date - startDate`, em dias. Negativo antes de a obra começar. */
  readonly elapsedDays: number;
  /** `totalDays - elapsedDays`. Negativo depois do término previsto. */
  readonly remainingDays: number;
}

/**
 * Os três números, ou nada.
 *
 * Obra sem início ou sem término previsto **não tem prazo**, e inventar um a partir da
 * data que existe daria um saldo que ninguém combinou. O cabeçalho simplesmente não
 * mostra a faixa — que é honesto, e é o que empurra alguém a preencher o cadastro.
 */
export function projectTerm(input: {
  startDate?: string | undefined;
  endDate?: string | undefined;
  date: string;
}): ProjectTerm | undefined {
  if (input.startDate === undefined || input.endDate === undefined) return undefined;

  const inicio = BusinessDate.parse(input.startDate);
  const fim = BusinessDate.parse(input.endDate);
  const dia = BusinessDate.parse(input.date);
  if (!inicio.ok || !fim.ok || !dia.ok) return undefined;

  const totalDays = inicio.value.daysUntil(fim.value);
  const elapsedDays = inicio.value.daysUntil(dia.value);

  return { totalDays, elapsedDays, remainingDays: totalDays - elapsedDays };
}
